import vtexProductListingPage, {
  type PLPProps,
} from "@decocms/apps-vtex/loaders/intelligentSearch/productListingPage";
import type { Product } from "@decocms/apps-commerce/types";
import { stripHeavyInstallments } from "~/utils/leanOffers";
import { withEdgeCache } from "~/utils/edgeCache";

/**
 * Vitrines automáticas por coleção (cluster VTEX). Quem monta a página
 * informa apenas o `collectionId` e este loader busca os produtos daquele
 * cluster, já no formato schema.org `Product[]` que as vitrines esperam.
 *
 * Reusa o loader de PLP da Intelligent Search
 * (`vtex/loaders/intelligentSearch/productListingPage.ts`) filtrando por
 * `selectedFacets: productClusterIds` — o MESMO mecanismo comprovado do
 * `shelfProductsFull.ts` (vitrines de coleção da home). Importante: esse
 * caminho devolve TODAS as SKUs de cada produto em `isVariantOf.hasVariant`
 * (o seletor de cor/tamanho da compra rápida depende disso). O vendor
 * `productListFull` (busca por `sku:`/facet path) NÃO servia aqui: colapsa
 * cada produto a uma única SKU representativa e, na prática, não trazia os
 * produtos da coleção — deixando a vitrine vazia.
 */
export interface Props {
  /** @title ID da coleção (cluster VTEX) */
  collectionId: number;
  /**
   * @title Quantidade de produtos
   * @description Máximo de produtos buscados por coleção (padrão: 12)
   */
  count?: number;
  /**
   * @title Ordenação
   * @description Valor de `sort` da Intelligent Search da VTEX (ex:
   * `orders:desc`, `discount:desc`, `release:desc`). Vazio = ordem padrão.
   */
  sort?: PLPProps["sort"];
  /**
   * Pula o cache de edge e vai direto na VTEX. Usado só pelo modo de debug
   * das vitrines (`?debug=tabcategories`), para provar se o que aparece na
   * página veio de cache ou de uma busca nova.
   * @ignore
   */
  bypassCache?: boolean;
}

const CACHE_TTL_SECONDS = 300;
const DEFAULT_COUNT = 12;

export default async function productsByCollectionId(
  { collectionId, count = DEFAULT_COUNT, sort, bypassCache }: Props,
): Promise<Product[] | null> {
  // Sem ID não há o que buscar — evita disparar uma query de cluster vazia
  // à VTEX (que voltaria com produtos aleatórios ou nada).
  if (collectionId === undefined || collectionId === null) return null;

  const collection = String(collectionId);
  const cacheKeyUrl =
    `https://edge-cache.internal/collection-list/${collection}/${count}/${sort ?? ""}`;

  const fetchProducts = async () => {
    const result = await vtexProductListingPage({
      selectedFacets: [{ key: "productClusterIds", value: collection }],
      count,
      sort,
    });

    const products = result?.products ?? null;
    return products
      ? products.slice(0, count).map(stripHeavyInstallments)
      : null;
  };

  if (bypassCache) return fetchProducts();

  return withEdgeCache(
    "collection-list-v1",
    cacheKeyUrl,
    CACHE_TTL_SECONDS,
    fetchProducts,
  );
}
